/**
 * FreshdeskAnalysePage — Admin-Modul (#905) fuer Freshdesk-Ticket-Auswertung.
 *
 * Tabs:
 *  - Config: Domain + API-Key + Feature-Flag + Limits
 *  - Thematik: Top-30 Cluster
 *  - Vermeidbarkeit: true/false/unklar
 *  - Agent-Roadmap: Archetypen + 2D-Scatter (Thematik x Feasibility x Risk x Value)
 *  - Ticket-Browser: Filterbare Tabelle
 *  - Sync: Backfill-Start + Live-Progress + History + Kosten-Dashboard
 *
 * @route /admin/freshdesk-analyse
 * @roles admin
 * @since v2.395.0
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import useTabParam from '../hooks/useTabParam';
import { ToggleSwitch } from '../components/ui';
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip as ReTooltip,
  CartesianGrid, ResponsiveContainer, Cell, BarChart, Bar, Legend,
} from 'recharts';

const API = import.meta.env.VITE_API_URL || '';

const RISK_COLORS = { low: '#10B981', medium: '#F59E0B', high: '#EF4444' };
const VALUE_SIZES = { low: 60, medium: 140, high: 260 };
const ARCHETYP_COLORS = {
  data_lookup: '#338edc',
  wms_write: '#6366F1',
  clarification: '#22D3EE',
  hybrid: '#8B5CF6',
  human_handoff: '#64748B',
};

function fmtEur(n) {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function fmtInt(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString('de-AT');
}

function daysAgoISO(days) {
  const d = new Date(Date.now() - days * 86400000);
  return d.toISOString().slice(0, 10);
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

async function fetchJson(url, opts = {}) {
  const r = await fetch(API + url, { credentials: 'include', ...opts });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
  return j;
}

export default function FreshdeskAnalysePage() {
  const { t } = useLanguage();
  const [tab, setTab] = useTabParam('config');

  const [config, setConfig] = useState(null);
  const [configSaving, setConfigSaving] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [domainInput, setDomainInput] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [tickets, setTickets] = useState({ rows: [], total: 0 });
  const [ticketFilters, setTicketFilters] = useState({ q: '', thematik: '', archetyp: '', risk: '', value: '', feasibility_min: '', feasibility_max: '' });
  const [ticketPage, setTicketPage] = useState(1);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketDetail, setTicketDetail] = useState(null);

  const [syncCurrent, setSyncCurrent] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [backfillVon, setBackfillVon] = useState(daysAgoISO(7));
  const [backfillBis, setBackfillBis] = useState(todayISO());
  const [estimate, setEstimate] = useState(null);
  const [estLoading, setEstLoading] = useState(false);
  const [startingRun, setStartingRun] = useState(false);
  const pollTimer = useRef(null);

  // ─── Report (#907) ──────────────────────────────────────
  const [reportVon, setReportVon] = useState(daysAgoISO(30));
  const [reportBis, setReportBis] = useState(todayISO());
  const [reportEstimate, setReportEstimate] = useState(null);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);
  const [reportArchive, setReportArchive] = useState([]);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const showMsg = (text) => { setMsg(text); setErr(''); setTimeout(() => setMsg(''), 4000); };
  const showErr = (text) => { setErr(text); setMsg(''); setTimeout(() => setErr(''), 6000); };

  // ─── Config ──────────────────────────────────────────
  const loadConfig = useCallback(async () => {
    try {
      const cfg = await fetchJson('/api/freshdesk/config');
      setConfig(cfg);
      setDomainInput(cfg.domain || '');
    } catch (e) { showErr(e.message); }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const saveConfig = async () => {
    setConfigSaving(true);
    try {
      const body = { domain: domainInput };
      if (apiKeyInput) body.api_key = apiKeyInput;
      await fetchJson('/api/freshdesk/config', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      setApiKeyInput('');
      await loadConfig();
      showMsg(t('freshdesk.msg.config_saved'));
    } catch (e) { showErr(e.message); }
    finally { setConfigSaving(false); }
  };

  const toggleFlag = async (key, value) => {
    try {
      await fetchJson('/api/einstellungen/' + key, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value }),
      });
      await loadConfig();
      showMsg(t('freshdesk.msg.flag_updated'));
    } catch (e) { showErr(e.message); }
  };

  const testConnection = async () => {
    setTestResult({ loading: true });
    try {
      const r = await fetchJson('/api/freshdesk/test-connection');
      setTestResult(r);
    } catch (e) { setTestResult({ ok: false, error: e.message }); }
  };

  // ─── Stats ──────────────────────────────────────────
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try { setStats(await fetchJson('/api/freshdesk/stats')); }
    catch (e) { showErr(e.message); }
    finally { setStatsLoading(false); }
  }, []);

  useEffect(() => {
    if (['thematik', 'vermeidbarkeit', 'agent', 'sync'].includes(tab)) loadStats();
  }, [tab, loadStats]);

  // ─── Tickets ──────────────────────────────────────────
  const loadTickets = useCallback(async () => {
    setTicketsLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(ticketFilters).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set('page', ticketPage);
      params.set('size', '50');
      const r = await fetchJson('/api/freshdesk/tickets?' + params);
      setTickets(r);
    } catch (e) { showErr(e.message); }
    finally { setTicketsLoading(false); }
  }, [ticketFilters, ticketPage]);

  useEffect(() => { if (tab === 'tickets') loadTickets(); }, [tab, loadTickets]);

  const openTicket = async (id) => {
    try {
      const r = await fetchJson('/api/freshdesk/tickets/' + id);
      setTicketDetail(r);
    } catch (e) { showErr(e.message); }
  };

  const reanalyseTicket = async (id) => {
    try {
      const r = await fetchJson('/api/freshdesk/tickets/' + id + '/analyse', { method: 'POST' });
      if (r.ok) showMsg(t('freshdesk.msg.reanalyse_ok'));
      else showErr(r.error || t('freshdesk.msg.reanalyse_failed'));
      await openTicket(id);
      loadTickets();
    } catch (e) { showErr(e.message); }
  };

  // ─── Sync / Backfill ──────────────────────────────────────────
  const loadSyncStatus = useCallback(async () => {
    try {
      const [curr, hist] = await Promise.all([
        fetchJson('/api/freshdesk/sync/current'),
        fetchJson('/api/freshdesk/sync/history'),
      ]);
      setSyncCurrent(curr.current || null);
      setSyncHistory(hist.history || []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    if (tab !== 'sync') {
      if (pollTimer.current) clearInterval(pollTimer.current);
      pollTimer.current = null;
      return;
    }
    loadSyncStatus();
    // Polling: 10s wenn laufender Sync, sonst 30s
    pollTimer.current = setInterval(() => {
      loadSyncStatus();
      if (syncCurrent?.status === 'running') loadStats();
    }, syncCurrent?.status === 'running' ? 10000 : 30000);
    return () => { if (pollTimer.current) clearInterval(pollTimer.current); };
  }, [tab, loadSyncStatus, syncCurrent?.status, loadStats]);

  const estimateCost = async () => {
    if (!backfillVon || !backfillBis) { showErr(t('freshdesk.msg.date_required')); return; }
    setEstLoading(true); setEstimate(null);
    try {
      const r = await fetchJson('/api/freshdesk/backfill/estimate?von=' + backfillVon + '&bis=' + backfillBis);
      setEstimate(r);
    } catch (e) { showErr(e.message); }
    finally { setEstLoading(false); }
  };

  const startBackfill = async () => {
    if (!backfillVon || !backfillBis) return;
    setStartingRun(true);
    try {
      const r = await fetchJson('/api/freshdesk/backfill/start', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ von: backfillVon, bis: backfillBis }),
      });
      showMsg(t('freshdesk.msg.backfill_started') + ' #' + r.sync_id);
      await loadSyncStatus();
    } catch (e) { showErr(e.message); }
    finally { setStartingRun(false); }
  };

  const cancelBackfill = async () => {
    if (!syncCurrent) return;
    if (!confirm(t('freshdesk.msg.cancel_confirm'))) return;
    try {
      await fetchJson('/api/freshdesk/backfill/' + syncCurrent.id + '/cancel', { method: 'POST' });
      await loadSyncStatus();
      showMsg(t('freshdesk.msg.cancelled'));
    } catch (e) { showErr(e.message); }
  };

  const runDailySync = async () => {
    try {
      const r = await fetchJson('/api/freshdesk/daily-sync/run', { method: 'POST' });
      showMsg(t('freshdesk.msg.daily_started'));
      await loadSyncStatus();
    } catch (e) { showErr(e.message); }
  };

  // ─── Report (#907) ──────────────────────────────────────
  const loadReportArchive = useCallback(async () => {
    try {
      const r = await fetchJson('/api/freshdesk/reports');
      setReportArchive(r.reports || []);
      if (!currentReport && r.reports?.[0]) {
        const detail = await fetchJson('/api/freshdesk/reports/' + r.reports[0].id);
        setCurrentReport(detail);
      }
    } catch (e) { /* silent on tab-load */ }
  }, [currentReport]);

  const estimateReportCost = useCallback(async () => {
    if (!reportVon || !reportBis) return;
    try {
      const r = await fetchJson('/api/freshdesk/reports/estimate?von=' + reportVon + '&bis=' + reportBis);
      setReportEstimate(r);
    } catch (e) { setReportEstimate(null); }
  }, [reportVon, reportBis]);

  useEffect(() => { if (tab === 'report') { loadReportArchive(); estimateReportCost(); } }, [tab, loadReportArchive, estimateReportCost]);

  const generateReport = async () => {
    if (!reportVon || !reportBis) return;
    setReportGenerating(true);
    try {
      const r = await fetchJson('/api/freshdesk/reports/generate', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ von: reportVon, bis: reportBis }),
      });
      const detail = await fetchJson('/api/freshdesk/reports/' + r.id);
      setCurrentReport(detail);
      await loadReportArchive();
      showMsg(t('freshdesk.report.generated_ok'));
    } catch (e) { showErr(e.message); }
    finally { setReportGenerating(false); }
  };

  const loadReport = async (id) => {
    try {
      const detail = await fetchJson('/api/freshdesk/reports/' + id);
      setCurrentReport(detail);
    } catch (e) { showErr(e.message); }
  };

  const deleteReport = async (id) => {
    if (!confirm(t('freshdesk.report.delete_confirm'))) return;
    try {
      await fetchJson('/api/freshdesk/reports/' + id, { method: 'DELETE' });
      if (currentReport?.id === id) setCurrentReport(null);
      await loadReportArchive();
      showMsg(t('freshdesk.report.deleted'));
    } catch (e) { showErr(e.message); }
  };

  const setReportPreset = (days) => {
    if (days === 'all') {
      setReportVon('2020-01-01');
      setReportBis(todayISO());
    } else {
      setReportVon(daysAgoISO(days));
      setReportBis(todayISO());
    }
  };

  const copyReportMarkdown = async () => {
    if (!currentReport?.markdown_content) return;
    try {
      await navigator.clipboard.writeText(currentReport.markdown_content);
      showMsg(t('freshdesk.report.copied'));
    } catch (e) { showErr(e.message); }
  };

  const setQuickRange = (days) => {
    if (days === 'voll') { setBackfillVon('2025-01-01'); setBackfillBis(todayISO()); }
    else { setBackfillVon(daysAgoISO(days)); setBackfillBis(todayISO()); }
    setEstimate(null);
  };

  // ─── Render ────────────────────────────────────────────
  const TABS = [
    { key: 'config', label: t('freshdesk.tab.config') },
    { key: 'thematik', label: t('freshdesk.tab.thematik') },
    { key: 'vermeidbarkeit', label: t('freshdesk.tab.vermeidbarkeit') },
    { key: 'agent', label: t('freshdesk.tab.agent') },
    { key: 'tickets', label: t('freshdesk.tab.tickets') },
    { key: 'sync', label: t('freshdesk.tab.sync') },
    { key: 'import', label: t('freshdesk.tab.import') },
    { key: 'report', label: t('freshdesk.tab.report') },
  ];

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">{t('freshdesk.title')}</h1>
        <p className="text-sm text-[var(--text-tertiary)]">{t('freshdesk.subtitle')}</p>
      </div>

      {msg && <div className="mb-4 p-3 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm">{msg}</div>}
      {err && <div className="mb-4 p-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-800 text-sm">{err}</div>}

      {/* Tab Bar */}
      <div className="mb-6 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] inline-flex">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            className={`px-4 py-1.5 text-sm rounded-[8px] transition-colors ${
              tab === tb.key
                ? 'bg-white shadow-sm text-[var(--text-primary)] font-medium'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {tab === 'config' && (
        <ConfigTab
          config={config}
          domainInput={domainInput} setDomainInput={setDomainInput}
          apiKeyInput={apiKeyInput} setApiKeyInput={setApiKeyInput}
          saveConfig={saveConfig} configSaving={configSaving}
          toggleFlag={toggleFlag}
          testConnection={testConnection} testResult={testResult}
          t={t}
        />
      )}

      {tab === 'thematik' && <ThematikTab stats={stats} loading={statsLoading} t={t} />}
      {tab === 'vermeidbarkeit' && <VermeidbarkeitTab stats={stats} loading={statsLoading} t={t} />}
      {tab === 'agent' && <AgentRoadmapTab stats={stats} loading={statsLoading} t={t} />}
      {tab === 'tickets' && (
        <TicketBrowserTab
          tickets={tickets} loading={ticketsLoading}
          filters={ticketFilters} setFilters={setTicketFilters}
          page={ticketPage} setPage={setTicketPage}
          onReload={loadTickets}
          onOpen={openTicket}
          detail={ticketDetail} closeDetail={() => setTicketDetail(null)}
          onReanalyse={reanalyseTicket}
          t={t}
        />
      )}
      {tab === 'sync' && (
        <SyncTab
          config={config}
          current={syncCurrent} history={syncHistory} stats={stats}
          von={backfillVon} setVon={setBackfillVon}
          bis={backfillBis} setBis={setBackfillBis}
          estimate={estimate} estLoading={estLoading} onEstimate={estimateCost}
          onStart={startBackfill} starting={startingRun}
          onCancel={cancelBackfill}
          onDailySync={runDailySync}
          onQuickRange={setQuickRange}
          t={t}
        />
      )}
      {tab === 'import' && <ImportTab t={t} />}
      {tab === 'report' && (
        <ReportTab
          von={reportVon} setVon={setReportVon}
          bis={reportBis} setBis={setReportBis}
          estimate={reportEstimate} onEstimate={estimateReportCost}
          onPreset={setReportPreset}
          onGenerate={generateReport} generating={reportGenerating}
          current={currentReport}
          archive={reportArchive}
          archiveOpen={archiveOpen} setArchiveOpen={setArchiveOpen}
          onLoad={loadReport}
          onDelete={deleteReport}
          onCopy={copyReportMarkdown}
          t={t}
        />
      )}
    </div>
  );
}

// ══════════ Config Tab ══════════

function ConfigTab({ config, domainInput, setDomainInput, apiKeyInput, setApiKeyInput, saveConfig, configSaving, toggleFlag, testConnection, testResult, t }) {
  if (!config) return <div className="text-sm text-[var(--text-tertiary)]">{t('common.loading')}…</div>;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-5">
        <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-4">{t('freshdesk.config.credentials')}</h2>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">{t('freshdesk.config.domain')}</label>
            <input
              type="text"
              placeholder="weship.freshdesk.com"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white focus:outline-none focus:border-[var(--primary,#338edc)]"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">
              {t('freshdesk.config.api_key')} {config.api_key_set && <span className="text-emerald-600 ml-2">({t('freshdesk.config.saved')})</span>}
            </label>
            <input
              type="password"
              placeholder={config.api_key_set ? '••••••••' : t('freshdesk.config.api_key_placeholder')}
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white focus:outline-none focus:border-[var(--primary,#338edc)]"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={saveConfig}
              disabled={configSaving}
              className="px-4 py-2 text-sm rounded-lg bg-[var(--primary,#338edc)] text-white font-medium hover:opacity-90 disabled:opacity-50"
            >
              {configSaving ? t('common.saving') : t('common.save')}
            </button>
            <button
              onClick={testConnection}
              className="px-4 py-2 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]"
            >
              {t('freshdesk.config.test')}
            </button>
          </div>
          {testResult && !testResult.loading && (
            <div className={`text-xs p-2 rounded-lg ${testResult.ok ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'}`}>
              {testResult.ok ? `✓ ${t('freshdesk.config.test_ok')}: ${testResult.agent?.name || '—'}` : `✗ ${testResult.error}`}
            </div>
          )}
        </div>
      </div>

      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-5">
        <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-4">{t('freshdesk.config.limits')}</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2">
            <div>
              <div className="text-sm font-medium text-[var(--text-primary)]">{t('freshdesk.config.enabled')}</div>
              <div className="text-xs text-[var(--text-tertiary)]">{t('freshdesk.config.enabled_hint')}</div>
            </div>
            <ToggleSwitch checked={!!config.enabled} onChange={(v) => toggleFlag('freshdesk_analysis_enabled', v)} />
          </div>
          <div className="border-t border-[var(--border-default,#E5E7EB)] pt-3 text-sm">
            <div className="flex justify-between py-1">
              <span className="text-[var(--text-secondary)]">{t('freshdesk.config.max_per_run')}</span>
              <span className="font-medium text-[var(--text-primary)]">{fmtEur(config.max_eur_per_run)}</span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-[var(--text-secondary)]">{t('freshdesk.config.monthly_budget')}</span>
              <span className="font-medium text-[var(--text-primary)]">{fmtEur(config.monthly_budget_eur)}</span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-[var(--text-secondary)]">{t('freshdesk.config.cost_per_ticket')}</span>
              <span className="font-medium text-[var(--text-primary)]">{fmtEur(config.cost_per_ticket_eur)}</span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-[var(--text-secondary)]">{t('freshdesk.config.concurrency')}</span>
              <span className="font-medium text-[var(--text-primary)]">{config.concurrency}</span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-[var(--text-secondary)]">Model</span>
              <span className="font-mono text-xs text-[var(--text-primary)]">{config.model}</span>
            </div>
          </div>
          <div className="text-xs text-[var(--text-tertiary)] leading-relaxed pt-2">
            {t('freshdesk.config.limits_hint')}
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════ Thematik Tab ══════════

function ThematikTab({ stats, loading, t }) {
  if (loading || !stats) return <div className="text-sm text-[var(--text-tertiary)]">{t('common.loading')}…</div>;
  const total = (stats.thematik || []).reduce((s, r) => s + r.anzahl, 0);
  return (
    <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-5">
      <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-4">{t('freshdesk.thematik.title')} · {fmtInt(total)} {t('freshdesk.thematik.tickets')}</h2>
      {stats.thematik.length === 0 ? (
        <div className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('freshdesk.thematik.empty')}</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs text-[var(--text-tertiary)] uppercase border-b border-[var(--border-default,#E5E7EB)]">
            <tr>
              <th className="text-left py-2">#</th>
              <th className="text-left py-2">{t('freshdesk.thematik.thema')}</th>
              <th className="text-right py-2">{t('freshdesk.thematik.anzahl')}</th>
              <th className="text-right py-2">%</th>
              <th className="text-left py-2 pl-6">{t('freshdesk.thematik.balken')}</th>
            </tr>
          </thead>
          <tbody>
            {stats.thematik.map((r, i) => {
              const pct = total > 0 ? (r.anzahl / total) * 100 : 0;
              return (
                <tr key={r.thematik} className="border-b border-[var(--border-default,#E5E7EB)] odd:bg-[var(--bg-inset,#F8F9FC)]/30">
                  <td className="py-2 text-[var(--text-tertiary)]">{i + 1}</td>
                  <td className="py-2 text-[var(--text-primary)] font-medium">{r.thematik}</td>
                  <td className="py-2 text-right text-[var(--text-primary)]">{fmtInt(r.anzahl)}</td>
                  <td className="py-2 text-right text-[var(--text-tertiary)]">{pct.toFixed(1)}</td>
                  <td className="py-2 pl-6">
                    <div className="h-2 bg-[var(--bg-inset,#F8F9FC)] rounded-full overflow-hidden w-48">
                      <div className="h-full bg-[var(--primary,#338edc)]" style={{ width: pct + '%' }} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ══════════ Vermeidbarkeit Tab ══════════

function VermeidbarkeitTab({ stats, loading, t }) {
  if (loading || !stats) return <div className="text-sm text-[var(--text-tertiary)]">{t('common.loading')}…</div>;
  const data = stats.vermeidbarkeit || [];
  const total = data.reduce((s, r) => s + r.anzahl, 0);
  const byKey = (k) => data.find((r) => String(r.vermeidbar) === k)?.anzahl || 0;
  const ja = byKey('true');
  const nein = byKey('false');
  const unklar = byKey('unclear') + byKey('null') + (data.find((r) => r.vermeidbar === null)?.anzahl || 0);
  const bars = [
    { key: 'ja', label: t('freshdesk.vermeid.ja'), anzahl: ja, color: '#EF4444' },
    { key: 'nein', label: t('freshdesk.vermeid.nein'), anzahl: nein, color: '#10B981' },
    { key: 'unklar', label: t('freshdesk.vermeid.unklar'), anzahl: unklar, color: '#9CA3AF' },
  ];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-5">
        <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-4">{t('freshdesk.vermeid.title')} · {fmtInt(total)} {t('freshdesk.thematik.tickets')}</h2>
        <div className="space-y-3">
          {bars.map((b) => {
            const pct = total > 0 ? (b.anzahl / total) * 100 : 0;
            return (
              <div key={b.key}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[var(--text-primary)]">{b.label}</span>
                  <span className="text-[var(--text-secondary)]">{fmtInt(b.anzahl)} ({pct.toFixed(1)}%)</span>
                </div>
                <div className="h-3 bg-[var(--bg-inset,#F8F9FC)] rounded-full overflow-hidden">
                  <div className="h-full" style={{ width: pct + '%', background: b.color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-5">
        <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-4">{t('freshdesk.vermeid.hint_title')}</h2>
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
          {t('freshdesk.vermeid.hint_body')}
        </p>
      </div>
    </div>
  );
}

// ══════════ Agent Roadmap Tab ══════════

function AgentRoadmapTab({ stats, loading, t }) {
  if (loading || !stats) return <div className="text-sm text-[var(--text-tertiary)]">{t('common.loading')}…</div>;
  const archetypes = stats.archetyp || [];
  const scatterRaw = stats.scatter || [];
  const scatterData = scatterRaw.map((s) => ({
    thematik: s.thematik,
    x: Number(s.feasibility) || 0,
    y: Number(s.anzahl) || 0,
    risk: s.risk || 'medium',
    value: s.value || 'medium',
  }));

  return (
    <div className="space-y-4">
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-5">
        <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-4">{t('freshdesk.agent.archetyp_title')}</h2>
        {archetypes.length === 0 ? (
          <div className="text-sm text-[var(--text-tertiary)] py-4">{t('freshdesk.thematik.empty')}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {archetypes.map((a) => (
              <div key={a.agent_archetyp} className="bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] rounded-lg p-4">
                <div className="w-3 h-3 rounded-full mb-2" style={{ background: ARCHETYP_COLORS[a.agent_archetyp] || '#64748B' }} />
                <div className="text-xs text-[var(--text-tertiary)] mb-1">{t('freshdesk.archetyp.' + a.agent_archetyp)}</div>
                <div className="text-2xl font-bold text-[var(--text-primary)]">{fmtInt(a.anzahl)}</div>
                <div className="text-xs text-[var(--text-tertiary)] mt-2">
                  {t('freshdesk.agent.avg_feasibility')}: <span className="font-medium">{a.avg_feasibility ?? '—'}</span><br />
                  {t('freshdesk.agent.high_risk')}: <span className="font-medium">{a.high_risk}</span><br />
                  {t('freshdesk.agent.high_value')}: <span className="font-medium">{a.high_value}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-5">
        <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-1">{t('freshdesk.agent.scatter_title')}</h2>
        <p className="text-xs text-[var(--text-tertiary)] mb-4">{t('freshdesk.agent.scatter_hint')}</p>
        {scatterData.length === 0 ? (
          <div className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('freshdesk.thematik.empty')}</div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart margin={{ top: 10, right: 30, left: 10, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis type="number" dataKey="x" domain={[0, 100]} name={t('freshdesk.agent.feasibility')} tick={{ fontSize: 11 }}>
                <label>Feasibility</label>
              </XAxis>
              <YAxis type="number" dataKey="y" name={t('freshdesk.agent.count')} tick={{ fontSize: 11 }} />
              <ZAxis type="number" range={[VALUE_SIZES.low, VALUE_SIZES.high]} />
              <ReTooltip
                cursor={{ strokeDasharray: '3 3' }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const p = payload[0].payload;
                  return (
                    <div className="bg-white border border-[var(--border-default,#E5E7EB)] rounded-lg shadow p-2 text-xs">
                      <div className="font-semibold">{p.thematik}</div>
                      <div>Feasibility: {p.x}</div>
                      <div>Tickets: {p.y}</div>
                      <div>Risk: <span style={{ color: RISK_COLORS[p.risk] }}>{p.risk}</span></div>
                      <div>Value: {p.value}</div>
                    </div>
                  );
                }}
              />
              <Scatter data={scatterData}>
                {scatterData.map((d, i) => (
                  <Cell key={i} fill={RISK_COLORS[d.risk] || '#64748B'} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        )}
        <div className="flex gap-4 text-xs text-[var(--text-tertiary)] mt-3">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: RISK_COLORS.low }} /> {t('freshdesk.agent.risk_low')}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: RISK_COLORS.medium }} /> {t('freshdesk.agent.risk_medium')}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: RISK_COLORS.high }} /> {t('freshdesk.agent.risk_high')}</span>
        </div>
      </div>
    </div>
  );
}

// ══════════ Ticket Browser Tab ══════════

function TicketBrowserTab({ tickets, loading, filters, setFilters, page, setPage, onReload, onOpen, detail, closeDetail, onReanalyse, t }) {
  const maxPage = Math.max(1, Math.ceil((tickets.total || 0) / 50));
  return (
    <div className="space-y-4">
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-2">
          <input type="text" placeholder={t('freshdesk.tb.search')} value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="px-3 py-1.5 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white col-span-2" />
          <input type="text" placeholder={t('freshdesk.tb.thematik')} value={filters.thematik} onChange={(e) => setFilters({ ...filters, thematik: e.target.value })}
            className="px-3 py-1.5 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white" />
          <select value={filters.archetyp} onChange={(e) => setFilters({ ...filters, archetyp: e.target.value })}
            className="px-3 py-1.5 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white">
            <option value="">{t('freshdesk.tb.all_archetyps')}</option>
            <option value="data_lookup">data_lookup</option>
            <option value="wms_write">wms_write</option>
            <option value="clarification">clarification</option>
            <option value="hybrid">hybrid</option>
            <option value="human_handoff">human_handoff</option>
          </select>
          <select value={filters.risk} onChange={(e) => setFilters({ ...filters, risk: e.target.value })}
            className="px-3 py-1.5 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white">
            <option value="">{t('freshdesk.tb.all_risk')}</option>
            <option value="low">low</option>
            <option value="medium">medium</option>
            <option value="high">high</option>
          </select>
          <select value={filters.value} onChange={(e) => setFilters({ ...filters, value: e.target.value })}
            className="px-3 py-1.5 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white">
            <option value="">{t('freshdesk.tb.all_value')}</option>
            <option value="low">low</option>
            <option value="medium">medium</option>
            <option value="high">high</option>
          </select>
          <button onClick={() => { setPage(1); onReload(); }}
            className="px-3 py-1.5 text-sm rounded-lg bg-[var(--primary,#338edc)] text-white font-medium">
            {t('common.apply')}
          </button>
        </div>
      </div>

      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-[var(--text-tertiary)]">{t('common.loading')}…</div>
        ) : tickets.rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-[var(--text-tertiary)]">{t('freshdesk.tb.empty')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-[var(--text-tertiary)] uppercase bg-[var(--bg-inset,#F8F9FC)] border-b border-[var(--border-default,#E5E7EB)]">
              <tr>
                <th className="text-left py-2 px-3">ID</th>
                <th className="text-left py-2 px-3">{t('freshdesk.tb.subject')}</th>
                <th className="text-left py-2 px-3">{t('freshdesk.tb.thematik')}</th>
                <th className="text-left py-2 px-3">{t('freshdesk.tb.archetyp')}</th>
                <th className="text-right py-2 px-3">Feas</th>
                <th className="text-center py-2 px-3">Risk</th>
                <th className="text-center py-2 px-3">Value</th>
                <th className="text-left py-2 px-3">{t('freshdesk.tb.created')}</th>
              </tr>
            </thead>
            <tbody>
              {tickets.rows.map((r) => (
                <tr key={r.id} onClick={() => onOpen(r.id)}
                  className="border-b border-[var(--border-default,#E5E7EB)] odd:bg-[var(--bg-inset,#F8F9FC)]/30 cursor-pointer hover:bg-[var(--bg-inset,#F8F9FC)]">
                  <td className="py-2 px-3 text-[var(--text-tertiary)]">{r.id}</td>
                  <td className="py-2 px-3 text-[var(--text-primary)] font-medium truncate max-w-xs">{r.subject}</td>
                  <td className="py-2 px-3 text-[var(--text-secondary)]">{r.thematik || '—'}</td>
                  <td className="py-2 px-3 text-[var(--text-secondary)]">{r.agent_archetyp || '—'}</td>
                  <td className="py-2 px-3 text-right text-[var(--text-primary)]">{r.automation_feasibility ?? '—'}</td>
                  <td className="py-2 px-3 text-center">
                    {r.automation_risk ? <span className="inline-block px-2 py-0.5 text-xs rounded-full" style={{ background: RISK_COLORS[r.automation_risk] + '33', color: RISK_COLORS[r.automation_risk] }}>{r.automation_risk}</span> : '—'}
                  </td>
                  <td className="py-2 px-3 text-center text-[var(--text-secondary)]">{r.automation_value || '—'}</td>
                  <td className="py-2 px-3 text-xs text-[var(--text-tertiary)]">{r.fd_created_at ? new Date(r.fd_created_at).toLocaleDateString('de-AT') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tickets.rows.length > 0 && (
          <div className="flex items-center justify-between p-3 text-sm border-t border-[var(--border-default,#E5E7EB)]">
            <div className="text-[var(--text-tertiary)]">{fmtInt(tickets.total)} {t('freshdesk.thematik.tickets')}</div>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)}
                className="px-3 py-1 rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-40">‹</button>
              <span className="text-[var(--text-primary)]">{page} / {maxPage}</span>
              <button disabled={page >= maxPage} onClick={() => setPage(page + 1)}
                className="px-3 py-1 rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-40">›</button>
            </div>
          </div>
        )}
      </div>

      {detail && <TicketDetailModal detail={detail} onClose={closeDetail} onReanalyse={onReanalyse} t={t} />}
    </div>
  );
}

function TicketDetailModal({ detail, onClose, onReanalyse, t }) {
  const tk = detail.ticket;
  const convs = detail.conversations || [];
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-card max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[var(--border-default,#E5E7EB)] flex justify-between items-start">
          <div>
            <div className="text-xs text-[var(--text-tertiary)] mb-1">#{tk.id} · {tk.fd_created_at ? new Date(tk.fd_created_at).toLocaleString('de-AT') : '—'}</div>
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">{tk.subject}</h2>
            <div className="text-sm text-[var(--text-secondary)]">{tk.requester_name} · {tk.requester_email}</div>
          </div>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] text-xl">×</button>
        </div>

        {tk.thematik ? (
          <div className="p-5 bg-[var(--bg-inset,#F8F9FC)] border-b border-[var(--border-default,#E5E7EB)]">
            <h3 className="text-xs font-semibold text-[var(--text-tertiary)] uppercase mb-2">{t('freshdesk.detail.analysis')}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><div className="text-xs text-[var(--text-tertiary)]">{t('freshdesk.tb.thematik')}</div><div className="font-medium">{tk.thematik}</div></div>
              <div><div className="text-xs text-[var(--text-tertiary)]">{t('freshdesk.tb.archetyp')}</div><div className="font-medium">{tk.agent_archetyp}</div></div>
              <div><div className="text-xs text-[var(--text-tertiary)]">Feasibility</div><div className="font-medium">{tk.automation_feasibility}</div></div>
              <div><div className="text-xs text-[var(--text-tertiary)]">Risk / Value</div><div className="font-medium">{tk.automation_risk} / {tk.automation_value}</div></div>
            </div>
            {tk.summary && <div className="mt-3 text-sm text-[var(--text-secondary)]"><b>Summary:</b> {tk.summary}</div>}
            {Array.isArray(tk.required_tools) && tk.required_tools.length > 0 && (
              <div className="mt-2 text-sm text-[var(--text-secondary)]">
                <b>Tools:</b> {tk.required_tools.join(', ')}
              </div>
            )}
            {tk.vermeidbar === true && <div className="mt-2 text-sm text-rose-600"><b>{t('freshdesk.detail.avoidable')}</b> {tk.vermeidung_grund}</div>}
            {tk.analyse_error && <div className="mt-2 text-sm text-rose-600"><b>Error:</b> {tk.analyse_error}</div>}
            <button onClick={() => onReanalyse(tk.id)} className="mt-3 px-3 py-1.5 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white hover:bg-[var(--bg-inset,#F8F9FC)]">
              {t('freshdesk.detail.reanalyse')}
            </button>
          </div>
        ) : (
          <div className="p-5 bg-[var(--bg-inset,#F8F9FC)] border-b border-[var(--border-default,#E5E7EB)] text-sm text-[var(--text-tertiary)]">
            {t('freshdesk.detail.no_analysis')}
            <button onClick={() => onReanalyse(tk.id)} className="ml-3 px-3 py-1 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white">
              {t('freshdesk.detail.reanalyse')}
            </button>
          </div>
        )}

        <div className="p-5">
          <h3 className="text-xs font-semibold text-[var(--text-tertiary)] uppercase mb-3">{t('freshdesk.detail.description')}</h3>
          <div className="text-sm text-[var(--text-primary)] whitespace-pre-wrap leading-relaxed mb-4">{tk.description_text || '—'}</div>

          <h3 className="text-xs font-semibold text-[var(--text-tertiary)] uppercase mb-3">{t('freshdesk.detail.conversations')} ({convs.length})</h3>
          <div className="space-y-3">
            {convs.map((c) => (
              <div key={c.id} className={`text-sm p-3 rounded-lg border ${c.private ? 'bg-amber-50 border-amber-200' : c.incoming ? 'bg-[var(--bg-inset,#F8F9FC)] border-[var(--border-default,#E5E7EB)]' : 'bg-[var(--primary,#338edc)]/5 border-[var(--primary,#338edc)]/20'}`}>
                <div className="text-xs text-[var(--text-tertiary)] mb-1">
                  {c.private && <span className="text-amber-600 font-semibold mr-2">[INTERN]</span>}
                  {c.incoming ? t('freshdesk.detail.customer') : t('freshdesk.detail.agent')} · {c.fd_created_at ? new Date(c.fd_created_at).toLocaleString('de-AT') : '—'}
                </div>
                <div className="whitespace-pre-wrap">{c.body_text || '—'}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════ Sync Tab ══════════

function SyncTab({ config, current, history, stats, von, setVon, bis, setBis, estimate, estLoading, onEstimate, onStart, starting, onCancel, onDailySync, onQuickRange, t }) {
  const running = current?.status === 'running';
  const kosten = stats?.kosten;

  return (
    <div className="space-y-4">
      {/* Kosten-Dashboard */}
      {kosten && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-4">
            <div className="text-xs text-[var(--text-tertiary)] mb-1">{t('freshdesk.sync.today')}</div>
            <div className="text-2xl font-bold text-[var(--text-primary)]">{fmtEur(kosten.heute_eur)}</div>
            <div className="text-xs text-[var(--text-tertiary)] mt-1">{kosten.heute_runs} Runs</div>
          </div>
          <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-4">
            <div className="text-xs text-[var(--text-tertiary)] mb-1">{t('freshdesk.sync.month')}</div>
            <div className="text-2xl font-bold text-[var(--text-primary)]">{fmtEur(kosten.monat_eur)}</div>
            <div className="text-xs text-[var(--text-tertiary)] mt-1">{kosten.monat_runs} Runs · {kosten.monatsbudget_prozent}% {t('freshdesk.sync.budget')}</div>
            <div className="h-1.5 bg-[var(--bg-inset,#F8F9FC)] rounded-full overflow-hidden mt-2">
              <div className="h-full" style={{ width: Math.min(100, kosten.monatsbudget_prozent) + '%', background: kosten.monatsbudget_prozent >= 100 ? '#EF4444' : kosten.monatsbudget_prozent >= 80 ? '#F59E0B' : '#10B981' }} />
            </div>
          </div>
          <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-4">
            <div className="text-xs text-[var(--text-tertiary)] mb-1">{t('freshdesk.sync.lifetime')}</div>
            <div className="text-2xl font-bold text-[var(--text-primary)]">{fmtEur(kosten.lifetime_eur)}</div>
            <div className="text-xs text-[var(--text-tertiary)] mt-1">{kosten.lifetime_runs} Runs</div>
          </div>
          <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-4">
            <div className="text-xs text-[var(--text-tertiary)] mb-1">{t('freshdesk.sync.gesamt')}</div>
            <div className="text-2xl font-bold text-[var(--text-primary)]">{fmtInt(stats.gesamt?.tickets)}</div>
            <div className="text-xs text-[var(--text-tertiary)] mt-1">{stats.gesamt?.analysed} {t('freshdesk.sync.analysed')} · {stats.gesamt?.analyse_errors} Err</div>
          </div>
        </div>
      )}

      {/* Backfill-Start */}
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-5">
        <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-4">{t('freshdesk.sync.backfill_title')}</h2>

        {!config?.enabled && (
          <div className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm">
            {t('freshdesk.sync.flag_off')}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-3">
          {[
            { days: 1, label: t('freshdesk.sync.q_24h') },
            { days: 7, label: t('freshdesk.sync.q_7d') },
            { days: 28, label: t('freshdesk.sync.q_4w') },
            { days: 90, label: t('freshdesk.sync.q_3m') },
            { days: 'voll', label: t('freshdesk.sync.q_full') },
          ].map((q) => (
            <button key={q.label} onClick={() => onQuickRange(q.days)}
              className="px-3 py-1.5 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]">
              {q.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">{t('freshdesk.sync.von')}</label>
            <input type="date" value={von} onChange={(e) => setVon(e.target.value)}
              className="w-full px-3 py-1.5 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white" />
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">{t('freshdesk.sync.bis')}</label>
            <input type="date" value={bis} onChange={(e) => setBis(e.target.value)}
              className="w-full px-3 py-1.5 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white" />
          </div>
          <div className="flex gap-2">
            <button onClick={onEstimate} disabled={estLoading}
              className="flex-1 px-4 py-2 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50">
              {estLoading ? '…' : t('freshdesk.sync.estimate')}
            </button>
          </div>
        </div>

        {estimate && (
          <div className="mt-4 p-4 rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><div className="text-xs text-[var(--text-tertiary)]">{t('freshdesk.sync.tickets')}</div><div className="text-lg font-semibold text-[var(--text-primary)]">{estimate.count < 0 ? '?' : fmtInt(estimate.count)}</div></div>
              <div><div className="text-xs text-[var(--text-tertiary)]">{t('freshdesk.sync.est_cost')}</div><div className="text-lg font-semibold text-[var(--text-primary)]">{fmtEur(estimate.estimated_cost_eur)}</div></div>
              <div><div className="text-xs text-[var(--text-tertiary)]">{t('freshdesk.sync.month_remaining')}</div><div className={`text-lg font-semibold ${estimate.month_remaining_eur < 0 ? 'text-rose-600' : 'text-[var(--text-primary)]'}`}>{fmtEur(estimate.month_remaining_eur)}</div></div>
              <div><div className="text-xs text-[var(--text-tertiary)]">{t('freshdesk.sync.max_run')}</div><div className="text-lg font-semibold text-[var(--text-primary)]">{fmtEur(estimate.max_eur_per_run)}</div></div>
            </div>
            {estimate.would_exceed_run_limit && (
              <div className="mt-3 p-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs">
                ⚠ {t('freshdesk.sync.exceed_run')}
              </div>
            )}
            {estimate.would_exceed_monthly && (
              <div className="mt-2 p-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs">
                ⚠ {t('freshdesk.sync.exceed_monthly')}
              </div>
            )}
            <button onClick={onStart} disabled={starting || running || estimate.would_exceed_run_limit || !config?.enabled}
              className="mt-3 w-full px-4 py-2 text-sm rounded-lg bg-[var(--primary,#338edc)] text-white font-medium hover:opacity-90 disabled:opacity-40">
              {starting ? t('freshdesk.sync.starting') : running ? t('freshdesk.sync.running') : t('freshdesk.sync.start')}
            </button>
          </div>
        )}

        <div className="mt-3 flex gap-2">
          <button onClick={onDailySync} disabled={running || !config?.enabled}
            className="px-3 py-1.5 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-40">
            {t('freshdesk.sync.run_daily')}
          </button>
        </div>
      </div>

      {/* Live-Progress */}
      {current && (
        <div className={`bg-[var(--bg-card,#fff)] border rounded-card p-5 ${current.is_stale ? 'border-amber-300' : 'border-[var(--border-default,#E5E7EB)]'}`}>
          <div className="flex justify-between items-start mb-3">
            <div>
              <h2 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
                {running ? '▶ ' : '■ '}
                {current.sync_type} #{current.id}
                {current.is_stale && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                    ⚠ {t('freshdesk.sync.stale_badge')}
                  </span>
                )}
              </h2>
              <div className="text-xs text-[var(--text-tertiary)] mt-1">
                {current.zeitraum_von} → {current.zeitraum_bis} · {t('freshdesk.sync.status')}: <span className="font-medium">{current.status}</span>
                {current.heartbeat_age_sec != null && (
                  <span className={`ml-2 ${current.is_stale ? 'text-amber-700 font-medium' : ''}`}>
                    · {t('freshdesk.sync.heartbeat')}: {current.heartbeat_age_sec}s
                  </span>
                )}
              </div>
            </div>
            {running && (
              <button onClick={onCancel}
                className={`px-3 py-1.5 text-xs rounded-lg border ${current.is_stale
                  ? 'border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100 font-medium'
                  : 'border-rose-300 text-rose-700 hover:bg-rose-50'}`}>
                {current.is_stale ? t('freshdesk.sync.cancel_stale') : t('freshdesk.sync.cancel')}
              </button>
            )}
          </div>
          {current.is_stale && (
            <div className="mb-3 p-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
              {t('freshdesk.sync.stale_hint')}
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <div><div className="text-xs text-[var(--text-tertiary)]">{t('freshdesk.sync.fetched')}</div><div className="font-semibold">{fmtInt(current.tickets_fetched)}</div></div>
            <div><div className="text-xs text-[var(--text-tertiary)]">{t('freshdesk.sync.analysed')}</div><div className="font-semibold">{fmtInt(current.tickets_analysed)}</div></div>
            <div><div className="text-xs text-[var(--text-tertiary)]">{t('freshdesk.sync.errors')}</div><div className="font-semibold text-rose-600">{fmtInt(current.errors_count)}</div></div>
            <div><div className="text-xs text-[var(--text-tertiary)]">{t('freshdesk.sync.cost_live')}</div><div className="font-semibold">{fmtEur(current.cost_eur)}</div></div>
            <div><div className="text-xs text-[var(--text-tertiary)]">{t('freshdesk.sync.budget_limit')}</div><div className="font-semibold">{fmtEur(current.budget_limit_eur)}</div></div>
          </div>
          {current.cursor_value && (
            <div className="mt-2 text-xs text-[var(--text-tertiary)] font-mono">cursor: {current.cursor_value}</div>
          )}
          {current.error_message && (
            <div className="mt-3 p-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs">{current.error_message}</div>
          )}
        </div>
      )}

      {/* History */}
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card overflow-hidden">
        <div className="p-5 border-b border-[var(--border-default,#E5E7EB)]">
          <h2 className="text-sm font-semibold text-[var(--text-primary)]">{t('freshdesk.sync.history')}</h2>
        </div>
        {history.length === 0 ? (
          <div className="p-8 text-center text-sm text-[var(--text-tertiary)]">{t('freshdesk.sync.no_history')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-[var(--text-tertiary)] uppercase bg-[var(--bg-inset,#F8F9FC)]">
              <tr>
                <th className="text-left py-2 px-3">#</th>
                <th className="text-left py-2 px-3">Type</th>
                <th className="text-left py-2 px-3">{t('freshdesk.sync.status')}</th>
                <th className="text-left py-2 px-3">{t('freshdesk.sync.period')}</th>
                <th className="text-right py-2 px-3">{t('freshdesk.sync.fetched')}</th>
                <th className="text-right py-2 px-3">{t('freshdesk.sync.analysed')}</th>
                <th className="text-right py-2 px-3">{t('freshdesk.sync.cost')}</th>
                <th className="text-left py-2 px-3">{t('freshdesk.sync.started')}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((r) => (
                <tr key={r.id} className="border-b border-[var(--border-default,#E5E7EB)] odd:bg-[var(--bg-inset,#F8F9FC)]/30">
                  <td className="py-2 px-3 text-[var(--text-tertiary)]">{r.id}</td>
                  <td className="py-2 px-3 text-[var(--text-secondary)]">{r.sync_type}</td>
                  <td className="py-2 px-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${
                      r.status === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                      r.status === 'running' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                      r.status === 'error' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                      r.status === 'cancelled' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                      r.status === 'budget_reached' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                      'bg-slate-50 text-slate-700 border border-slate-200'
                    }`}>{r.status}</span>
                  </td>
                  <td className="py-2 px-3 text-xs text-[var(--text-secondary)]">{r.zeitraum_von} → {r.zeitraum_bis}</td>
                  <td className="py-2 px-3 text-right">{fmtInt(r.tickets_fetched)}</td>
                  <td className="py-2 px-3 text-right">{fmtInt(r.tickets_analysed)}</td>
                  <td className="py-2 px-3 text-right">{fmtEur(r.cost_eur)}</td>
                  <td className="py-2 px-3 text-xs text-[var(--text-tertiary)]">{r.started_at ? new Date(r.started_at).toLocaleString('de-AT') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ══════════ Report Tab (#907) ══════════

function ReportTab({
  von, setVon, bis, setBis,
  estimate, onEstimate, onPreset,
  onGenerate, generating,
  current, archive,
  archiveOpen, setArchiveOpen,
  onLoad, onDelete, onCopy,
  t,
}) {
  return (
    <div className="space-y-4">
      {/* Header-Controls */}
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-5">
        <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-3">{t('freshdesk.report.title')}</h2>
        <p className="text-xs text-[var(--text-tertiary)] mb-4">{t('freshdesk.report.subtitle')}</p>

        <div className="flex flex-wrap items-end gap-3 mb-3">
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">{t('freshdesk.report.range_from')}</label>
            <input
              type="date"
              value={von}
              onChange={(e) => setVon(e.target.value)}
              onBlur={onEstimate}
              className="px-3 py-2 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white focus:outline-none focus:border-[var(--primary,#338edc)]"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">{t('freshdesk.report.range_to')}</label>
            <input
              type="date"
              value={bis}
              onChange={(e) => setBis(e.target.value)}
              onBlur={onEstimate}
              className="px-3 py-2 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white focus:outline-none focus:border-[var(--primary,#338edc)]"
            />
          </div>
          <button onClick={() => { onPreset(7); setTimeout(onEstimate, 0); }} className="px-3 py-2 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)]">{t('freshdesk.report.preset_7d')}</button>
          <button onClick={() => { onPreset(30); setTimeout(onEstimate, 0); }} className="px-3 py-2 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)]">{t('freshdesk.report.preset_30d')}</button>
          <button onClick={() => { onPreset(90); setTimeout(onEstimate, 0); }} className="px-3 py-2 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)]">{t('freshdesk.report.preset_3m')}</button>
          <button onClick={() => { onPreset('all'); setTimeout(onEstimate, 0); }} className="px-3 py-2 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)]">{t('freshdesk.report.preset_all')}</button>

          <div className="ml-auto flex items-center gap-3">
            {estimate && (
              <span className={`text-xs px-3 py-1.5 rounded-lg ${estimate.within_budget ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'}`}>
                {t('freshdesk.report.cost_estimate').replace('{{cost}}', Number(estimate.estimated_cost_eur).toFixed(2))}
                {' · '}
                {t('freshdesk.report.tickets_in_range').replace('{{n}}', estimate.analysed_tickets)}
              </span>
            )}
            <button
              onClick={onGenerate}
              disabled={generating || (estimate && !estimate.within_budget) || (estimate?.analysed_tickets === 0)}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-[var(--primary,#338edc)] text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generating ? t('freshdesk.report.generating') : t('freshdesk.report.generate_button')}
            </button>
          </div>
        </div>

        {estimate && !estimate.within_budget && (
          <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2">
            {t('freshdesk.report.budget_exceeded').replace('{{max}}', Number(estimate.max_eur_per_run).toFixed(2))}
          </div>
        )}
        {estimate?.analysed_tickets === 0 && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
            {t('freshdesk.report.no_tickets_in_range')}
          </div>
        )}
      </div>

      {/* Aktueller Report */}
      {!current && (
        <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-8 text-center text-sm text-[var(--text-tertiary)]">
          {t('freshdesk.report.no_reports')}
        </div>
      )}
      {current && (
        <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-5">
          {/* Meta-Row */}
          <div className="flex flex-wrap items-center gap-4 text-xs text-[var(--text-tertiary)] mb-4 pb-3 border-b border-[var(--border-default,#E5E7EB)]">
            <span><strong className="text-[var(--text-secondary)]">{t('freshdesk.report.meta.range')}:</strong> {current.zeitraum_von} – {current.zeitraum_bis}</span>
            <span><strong className="text-[var(--text-secondary)]">{t('freshdesk.report.meta.ticket_count')}:</strong> {current.ticket_count}</span>
            <span><strong className="text-[var(--text-secondary)]">{t('freshdesk.report.meta.generated_at')}:</strong> {new Date(current.generated_at).toLocaleString('de-AT')}</span>
            <span><strong className="text-[var(--text-secondary)]">{t('freshdesk.report.meta.generated_by')}:</strong> {current.generated_by_name || '—'}</span>
            <span><strong className="text-[var(--text-secondary)]">{t('freshdesk.report.meta.cost')}:</strong> {Number(current.cost_eur || 0).toFixed(4)} €</span>
            <span><strong className="text-[var(--text-secondary)]">{t('freshdesk.report.meta.model')}:</strong> {current.model}</span>

            <div className="ml-auto flex gap-2">
              <a
                href={API + '/api/freshdesk/reports/' + current.id + '/pdf'}
                download
                className="px-3 py-1.5 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)]"
              >
                {t('freshdesk.report.download_pdf')}
              </a>
              <button onClick={onCopy} className="px-3 py-1.5 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)]">{t('freshdesk.report.copy_markdown')}</button>
              <button onClick={() => onDelete(current.id)} className="px-3 py-1.5 text-xs rounded-lg border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700">{t('freshdesk.report.delete')}</button>
            </div>
          </div>
          {/* Markdown-Viewer (Plain-Render, kein React-Markdown — wir styling-approximieren) */}
          <MarkdownView markdown={current.markdown_content} />
        </div>
      )}

      {/* Archiv */}
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-5">
        <button
          onClick={() => setArchiveOpen((o) => !o)}
          className="flex items-center justify-between w-full text-left"
        >
          <span className="text-sm font-semibold text-[var(--text-primary)]">{t('freshdesk.report.archive_title')} ({archive.length})</span>
          <span className="text-xs text-[var(--text-tertiary)]">{archiveOpen ? '▲' : '▼'}</span>
        </button>
        {archiveOpen && (
          <div className="mt-4">
            {archive.length === 0 ? (
              <div className="text-sm text-[var(--text-tertiary)] py-4">{t('freshdesk.report.archive_empty')}</div>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[var(--text-tertiary)] border-b border-[var(--border-default,#E5E7EB)]">
                    <th className="py-2 font-medium">{t('freshdesk.report.meta.range')}</th>
                    <th className="py-2 font-medium">{t('freshdesk.report.meta.ticket_count')}</th>
                    <th className="py-2 font-medium">{t('freshdesk.report.meta.generated_at')}</th>
                    <th className="py-2 font-medium">{t('freshdesk.report.meta.generated_by')}</th>
                    <th className="py-2 font-medium text-right">{t('freshdesk.report.meta.cost')}</th>
                    <th className="py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {archive.map((r) => (
                    <tr key={r.id} className="border-b border-[var(--border-default,#E5E7EB)] hover:bg-[var(--bg-inset,#F8F9FC)] cursor-pointer" onClick={() => onLoad(r.id)}>
                      <td className="py-2 text-[var(--text-primary)]">{r.zeitraum_von} – {r.zeitraum_bis}</td>
                      <td className="py-2 text-[var(--text-secondary)]">{r.ticket_count}</td>
                      <td className="py-2 text-[var(--text-secondary)]">{new Date(r.generated_at).toLocaleString('de-AT')}</td>
                      <td className="py-2 text-[var(--text-secondary)]">{r.generated_by_name || '—'}</td>
                      <td className="py-2 text-right text-[var(--text-secondary)]">{Number(r.cost_eur || 0).toFixed(4)} €</td>
                      <td className="py-2 text-right">
                        {r.error_message && <span className="text-rose-600">⚠ {t('freshdesk.report.error_badge')}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MarkdownView({ markdown }) {
  if (!markdown) return null;
  const lines = String(markdown).split('\n');
  const blocks = [];
  let inList = false;
  let listItems = [];
  const flushList = () => {
    if (listItems.length) {
      blocks.push(<ul key={`ul-${blocks.length}`} className="list-disc ml-6 text-sm text-[var(--text-primary)] space-y-1 my-2">{listItems.map((it, i) => <li key={i}>{it}</li>)}</ul>);
      listItems = [];
      inList = false;
    }
  };
  lines.forEach((raw, idx) => {
    const line = raw.replace(/\*\*(.+?)\*\*/g, '$1').replace(/`(.+?)`/g, '$1');
    const trimmed = line.trim();
    if (!trimmed) { flushList(); return; }
    if (trimmed.startsWith('# ')) {
      flushList();
      blocks.push(<h1 key={idx} className="text-2xl font-bold text-[var(--text-primary)] mt-4 mb-3">{trimmed.slice(2)}</h1>);
    } else if (trimmed.startsWith('## ')) {
      flushList();
      blocks.push(<h2 key={idx} className="text-lg font-semibold text-[var(--primary,#338edc)] mt-5 mb-2 pb-1 border-b border-[var(--border-default,#E5E7EB)]">{trimmed.slice(3)}</h2>);
    } else if (trimmed.startsWith('### ')) {
      flushList();
      blocks.push(<h3 key={idx} className="text-sm font-semibold text-[var(--text-primary)] mt-3 mb-1">{trimmed.slice(4)}</h3>);
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      inList = true;
      listItems.push(trimmed.slice(2));
    } else if (/^\d+\.\s/.test(trimmed)) {
      flushList();
      blocks.push(<p key={idx} className="text-sm text-[var(--text-primary)] my-1 ml-4">{trimmed}</p>);
    } else {
      flushList();
      blocks.push(<p key={idx} className="text-sm text-[var(--text-primary)] my-2 leading-relaxed">{trimmed}</p>);
    }
  });
  flushList();
  return <div className="prose-sm max-w-none">{blocks}</div>;
}

// ══════════ Import Tab (#1333) ══════════
function ImportTab({ t }) {
  const [options, setOptions] = useState(null);
  const [optErr, setOptErr] = useState('');
  const [statusMapping, setStatusMapping] = useState([]); // [{freshdesk_status, label, weship_stage}]

  const [agentId, setAgentId] = useState('');
  const [selStatuses, setSelStatuses] = useState([]); // number[]
  const [von, setVon] = useState(daysAgoISO(7));
  const [bis, setBis] = useState(todayISO());
  const [tag, setTag] = useState('freshdesk');
  const [extraTags, setExtraTags] = useState('');

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [running, setRunning] = useState(false);

  const [agentEdits, setAgentEdits] = useState({}); // fdAgentId -> userId('' = none)
  const [statusEdits, setStatusEdits] = useState({}); // fdStatus -> stage
  const [savingKey, setSavingKey] = useState('');

  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const showMsg = (x) => { setMsg(x); setErr(''); setTimeout(() => setMsg(''), 4000); };
  const showErr = (x) => { setErr(x); setMsg(''); setTimeout(() => setErr(''), 6000); };

  const stageLabel = useCallback((k) => (k ? t(`freshdesk.import.stage.${k}`) : '—'), [t]);
  const fdStatusLabel = useCallback((v) => t(`freshdesk.import.fdstatus.${v}`), [t]);

  const loadOptions = useCallback(async () => {
    try {
      const o = await fetchJson('/api/freshdesk/import/options');
      setOptions(o);
      // Vorbelegen der Agent-Edits mit gemapptem oder vorgeschlagenem User
      const aEdits = {};
      for (const a of (o.agents || [])) {
        aEdits[a.freshdesk_agent_id] = (a.mapped_user_id ?? a.suggested_user_id ?? '') || '';
      }
      setAgentEdits(aEdits);
    } catch (e) { setOptErr(e.message); }
  }, []);

  const loadMappings = useCallback(async () => {
    try {
      const m = await fetchJson('/api/freshdesk/import/mappings');
      setStatusMapping(m.status_mapping || []);
      const sEdits = {};
      for (const r of (m.status_mapping || [])) sEdits[r.freshdesk_status] = r.weship_stage;
      setStatusEdits(sEdits);
    } catch (e) { setOptErr(e.message); }
  }, []);

  useEffect(() => { loadOptions(); loadMappings(); }, [loadOptions, loadMappings]);

  const toggleStatus = (v) => {
    setSelStatuses((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]);
  };

  const baseBody = () => ({
    agent_id: agentId === '' ? null : Number(agentId),
    von: von || null,
    bis: bis || null,
  });

  // Status-Mehrfachauswahl → eine API-Anfrage je Status (Backend filtert single-status). Leer = alle.
  const statusCalls = () => (selStatuses.length ? selStatuses : [null]);

  const doPreview = async () => {
    setPreviewing(true); setRunResult(null); setErr(''); setPreview(null);
    try {
      const merged = { total_matched: 0, fetched: 0, capped: false, items: [],
        summary: { would_create: 0, already_imported: 0, kunde_unassigned: null } };
      for (const s of statusCalls()) {
        const r = await fetchJson('/api/freshdesk/import/preview', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...baseBody(), status: s }),
        });
        merged.total_matched += r.total_matched || 0;
        merged.fetched += r.fetched || 0;
        merged.capped = merged.capped || !!r.capped;
        merged.summary.would_create += r.summary?.would_create || 0;
        merged.summary.already_imported += r.summary?.already_imported || 0;
        // Kunde wird erst beim echten Import aufgelöst (Vorschau ist schlank) → null = "beim Import"
        if (r.summary?.kunde_unassigned != null) {
          merged.summary.kunde_unassigned = (merged.summary.kunde_unassigned || 0) + r.summary.kunde_unassigned;
        }
        merged.items.push(...(r.items || []));
      }
      setPreview(merged);
    } catch (e) { showErr(e.message); }
    finally { setPreviewing(false); }
  };

  const doRun = async () => {
    if (!window.confirm(t('freshdesk.import.import_confirm'))) return;
    setRunning(true); setErr('');
    try {
      const extra = extraTags.split(',').map((s) => s.trim()).filter(Boolean);
      const merged = { capped: false, created: [], skipped: [], errors: [],
        summary: { created: 0, skipped: 0, errors: 0 } };
      for (const s of statusCalls()) {
        const r = await fetchJson('/api/freshdesk/import/run', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...baseBody(), status: s, tag: tag || 'freshdesk', extra_tags: extra }),
        });
        merged.capped = merged.capped || !!r.capped;
        merged.created.push(...(r.created || []));
        merged.skipped.push(...(r.skipped || []));
        merged.errors.push(...(r.errors || []));
        merged.summary.created += r.summary?.created || 0;
        merged.summary.skipped += r.summary?.skipped || 0;
        merged.summary.errors += r.summary?.errors || 0;
      }
      setRunResult(merged);
      showMsg(t('freshdesk.import.run_done'));
      loadMappings();
    } catch (e) { showErr(e.message); }
    finally { setRunning(false); }
  };

  const saveAgentMapping = async (a) => {
    setSavingKey(`a${a.freshdesk_agent_id}`);
    try {
      const uid = agentEdits[a.freshdesk_agent_id];
      await fetchJson('/api/freshdesk/import/mappings/agent', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          freshdesk_agent_id: a.freshdesk_agent_id,
          user_id: uid === '' ? null : Number(uid),
          freshdesk_agent_email: a.freshdesk_agent_email,
          freshdesk_agent_name: a.freshdesk_agent_name,
        }),
      });
      showMsg(t('freshdesk.import.saved'));
      loadOptions();
    } catch (e) { showErr(e.message); }
    finally { setSavingKey(''); }
  };

  const saveStatusMapping = async (fdStatus) => {
    setSavingKey(`s${fdStatus}`);
    try {
      await fetchJson('/api/freshdesk/import/mappings/status', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ freshdesk_status: fdStatus, weship_stage: statusEdits[fdStatus] }),
      });
      showMsg(t('freshdesk.import.saved'));
      loadMappings();
    } catch (e) { showErr(e.message); }
    finally { setSavingKey(''); }
  };

  const stages = options?.stages || [];
  const users = options?.users || [];
  const agents = options?.agents || [];

  return (
    <div className="space-y-4">
      {msg && <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm">{msg}</div>}
      {err && <div className="p-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-800 text-sm">{err}</div>}
      {optErr && <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm">{optErr}</div>}

      {/* ── Filter ── */}
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-5">
        <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-1">{t('freshdesk.import.filter_title')}</h2>
        <p className="text-xs text-[var(--text-tertiary)] mb-4">{t('freshdesk.import.subtitle')}</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">{t('freshdesk.import.agent')}</label>
            <select value={agentId} onChange={(e) => setAgentId(e.target.value)}
              className="w-full px-3 py-1.5 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white">
              <option value="">{t('freshdesk.import.agent_all')}</option>
              {agents.map((a) => (
                <option key={a.freshdesk_agent_id} value={a.freshdesk_agent_id}>
                  {a.freshdesk_agent_name || a.freshdesk_agent_email || a.freshdesk_agent_id}
                </option>
              ))}
            </select>
            {options?.agents_error && <p className="text-xs text-amber-600 mt-1">{t('freshdesk.import.no_agents')}</p>}
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">{t('freshdesk.import.date_from')}</label>
            <input type="date" value={von} onChange={(e) => setVon(e.target.value)}
              className="w-full px-3 py-1.5 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white" />
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">{t('freshdesk.import.date_to')}</label>
            <input type="date" value={bis} onChange={(e) => setBis(e.target.value)}
              className="w-full px-3 py-1.5 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white" />
          </div>
        </div>

        <div className="mb-3">
          <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">{t('freshdesk.import.status')}</label>
          <div className="flex flex-wrap gap-2">
            {(options?.statuses || []).map((s) => (
              <button key={s.value} type="button" onClick={() => toggleStatus(s.value)}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  selStatuses.includes(s.value)
                    ? 'bg-[var(--primary,#338edc)] text-white border-[var(--primary,#338edc)]'
                    : 'border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]'}`}>
                {fdStatusLabel(s.value)}
              </button>
            ))}
          </div>
          <p className="text-xs text-[var(--text-tertiary)] mt-1">{t('freshdesk.import.status_hint')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">{t('freshdesk.import.tag')}</label>
            <input type="text" value={tag} onChange={(e) => setTag(e.target.value)}
              className="w-full px-3 py-1.5 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white" />
            <p className="text-xs text-[var(--text-tertiary)] mt-1">{t('freshdesk.import.tag_hint')}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">{t('freshdesk.import.extra_tags')}</label>
            <input type="text" value={extraTags} onChange={(e) => setExtraTags(e.target.value)} placeholder="tag1, tag2"
              className="w-full px-3 py-1.5 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white" />
            <p className="text-xs text-[var(--text-tertiary)] mt-1">{t('freshdesk.import.extra_tags_hint')}</p>
          </div>
        </div>

        <button onClick={doPreview} disabled={previewing}
          className="px-4 py-2 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50">
          {previewing ? t('freshdesk.import.previewing') : t('freshdesk.import.preview_btn')}
        </button>
      </div>

      {/* ── Vorschau ── */}
      {preview && (
        <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">{t('freshdesk.import.preview_title')}</h2>
            <span className="text-xs text-[var(--text-tertiary)]">{t('freshdesk.import.dry_run_note')}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="p-3 rounded-lg bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)]">
              <div className="text-xs text-[var(--text-tertiary)]">{t('freshdesk.import.count_would_create')}</div>
              <div className="text-2xl font-bold text-[var(--text-primary)]">{fmtInt(preview.summary.would_create)}</div>
            </div>
            <div className="p-3 rounded-lg bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)]">
              <div className="text-xs text-[var(--text-tertiary)]">{t('freshdesk.import.count_already')}</div>
              <div className="text-2xl font-bold text-[var(--text-primary)]">{fmtInt(preview.summary.already_imported)}</div>
            </div>
            <div className="p-3 rounded-lg bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)]">
              <div className="text-xs text-[var(--text-tertiary)]">{t('freshdesk.import.count_unassigned')}</div>
              <div className="text-2xl font-bold text-[var(--text-primary)]">
                {preview.summary.kunde_unassigned == null
                  ? <span className="text-base font-medium text-[var(--text-tertiary)]">{t('freshdesk.import.kunde_at_import')}</span>
                  : fmtInt(preview.summary.kunde_unassigned)}
              </div>
            </div>
            <div className="p-3 rounded-lg bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)]">
              <div className="text-xs text-[var(--text-tertiary)]">{t('freshdesk.import.count_matched')}</div>
              <div className="text-2xl font-bold text-[var(--text-primary)]">{fmtInt(preview.total_matched)}</div>
            </div>
          </div>
          {preview.capped && <div className="mb-3 p-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs">⚠ {t('freshdesk.import.capped')}</div>}

          <div className="overflow-x-auto border border-[var(--border-default,#E5E7EB)] rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--bg-inset,#F8F9FC)] text-left text-xs text-[var(--text-tertiary)]">
                  <th className="px-3 py-2">{t('freshdesk.import.col_ticket')}</th>
                  <th className="px-3 py-2">{t('freshdesk.import.col_subject')}</th>
                  <th className="px-3 py-2">{t('freshdesk.import.col_action')}</th>
                  <th className="px-3 py-2">{t('freshdesk.import.col_kunde')}</th>
                  <th className="px-3 py-2">{t('freshdesk.import.col_agent')}</th>
                  <th className="px-3 py-2">{t('freshdesk.import.col_stage')}</th>
                </tr>
              </thead>
              <tbody>
                {preview.items.map((it) => (
                  <tr key={it.freshdesk_ticket_id} className="border-t border-[var(--border-default,#E5E7EB)]">
                    <td className="px-3 py-2 text-[var(--text-secondary)]">#{it.freshdesk_ticket_id}</td>
                    <td className="px-3 py-2 text-[var(--text-primary)] max-w-[260px] truncate">{it.subject || (it.error ? `⚠ ${it.error}` : '—')}</td>
                    <td className="px-3 py-2">
                      {it.action === 'already_imported'
                        ? <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">{t('freshdesk.import.action_already')}</span>
                        : it.action === 'would_create'
                          ? <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700">{t('freshdesk.import.action_would_create')}</span>
                          : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {it.kunde_deferred
                        ? <span className="text-[var(--text-tertiary)] text-xs">{t('freshdesk.import.kunde_at_import')}</span>
                        : it.kunde_assigned
                          ? <span className="text-[var(--text-primary)]">{it.kunde?.name}</span>
                          : <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">{t('freshdesk.import.unassigned')}</span>}
                    </td>
                    <td className="px-3 py-2">
                      {it.agent_matched
                        ? <span className="text-[var(--text-secondary)]">{it.agent_email || '✓'}</span>
                        : <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-500">{t('freshdesk.import.agent_unmapped')}</span>}
                    </td>
                    <td className="px-3 py-2 text-[var(--text-secondary)]">{stageLabel(it.target_stage)}</td>
                  </tr>
                ))}
                {preview.items.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-[var(--text-tertiary)]">{t('freshdesk.import.no_results')}</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <button onClick={doRun} disabled={running || preview.summary.would_create === 0}
            className="mt-4 w-full px-4 py-2 text-sm rounded-lg bg-[var(--primary,#338edc)] text-white font-medium hover:opacity-90 disabled:opacity-40">
            {running ? t('freshdesk.import.importing') : t('freshdesk.import.import_btn')}
          </button>
        </div>
      )}

      {/* ── Ergebnis ── */}
      {runResult && (
        <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-5">
          <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-3">{t('freshdesk.import.result_title')}</h2>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
              <div className="text-xs text-emerald-700">{t('freshdesk.import.res_created')}</div>
              <div className="text-2xl font-bold text-emerald-800">{fmtInt(runResult.summary.created)}</div>
            </div>
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
              <div className="text-xs text-slate-600">{t('freshdesk.import.res_skipped')}</div>
              <div className="text-2xl font-bold text-slate-700">{fmtInt(runResult.summary.skipped)}</div>
            </div>
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200">
              <div className="text-xs text-rose-700">{t('freshdesk.import.res_errors')}</div>
              <div className="text-2xl font-bold text-rose-800">{fmtInt(runResult.summary.errors)}</div>
            </div>
          </div>

          {runResult.created.length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-medium text-[var(--text-secondary)] mb-2">{t('freshdesk.import.created_title')}</div>
              <div className="flex flex-wrap gap-2">
                {runResult.created.map((c) => (
                  <a key={c.case_id} href={`/cases/${c.case_id}`} target="_blank" rel="noreferrer"
                    className="px-2 py-1 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--primary,#338edc)] hover:bg-[var(--bg-inset,#F8F9FC)]">
                    Case #{c.case_id} · #{c.freshdesk_ticket_id} · {c.comments}💬 {c.attachments}📎
                  </a>
                ))}
              </div>
            </div>
          )}
          {runResult.skipped.length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-medium text-[var(--text-secondary)] mb-1">{t('freshdesk.import.skipped_title')}</div>
              <div className="text-xs text-[var(--text-tertiary)]">{runResult.skipped.map((id) => `#${id}`).join(', ')}</div>
            </div>
          )}
          {runResult.errors.length > 0 && (
            <div>
              <div className="text-xs font-medium text-rose-700 mb-1">{t('freshdesk.import.errors_title')}</div>
              <ul className="text-xs text-rose-600 space-y-0.5">
                {runResult.errors.map((e) => <li key={e.freshdesk_ticket_id}>#{e.freshdesk_ticket_id}: {e.error}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ── Mappings ── */}
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-5">
        <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-1">{t('freshdesk.import.status_mapping_title')}</h2>
        <p className="text-xs text-[var(--text-tertiary)] mb-3">{t('freshdesk.import.status_mapping_hint')}</p>
        <div className="space-y-2">
          {statusMapping.map((r) => (
            <div key={r.freshdesk_status} className="flex items-center gap-3">
              <span className="w-28 text-sm text-[var(--text-secondary)]">{fdStatusLabel(r.freshdesk_status)}</span>
              <span className="text-[var(--text-tertiary)]">→</span>
              <select value={statusEdits[r.freshdesk_status] || ''}
                onChange={(e) => setStatusEdits((p) => ({ ...p, [r.freshdesk_status]: e.target.value }))}
                className="px-3 py-1.5 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white">
                {stages.map((s) => <option key={s} value={s}>{stageLabel(s)}</option>)}
              </select>
              <button onClick={() => saveStatusMapping(r.freshdesk_status)} disabled={savingKey === `s${r.freshdesk_status}`}
                className="px-3 py-1.5 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50">
                {t('freshdesk.import.save')}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-5">
        <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-1">{t('freshdesk.import.agent_mapping_title')}</h2>
        <p className="text-xs text-[var(--text-tertiary)] mb-3">{t('freshdesk.import.agent_mapping_hint')}</p>
        {options?.agents_error && <div className="mb-3 p-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs">{t('freshdesk.import.no_agents')}</div>}
        <div className="overflow-x-auto border border-[var(--border-default,#E5E7EB)] rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--bg-inset,#F8F9FC)] text-left text-xs text-[var(--text-tertiary)]">
                <th className="px-3 py-2">{t('freshdesk.import.col_fd_agent')}</th>
                <th className="px-3 py-2">{t('freshdesk.import.col_ma')}</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr key={a.freshdesk_agent_id} className="border-t border-[var(--border-default,#E5E7EB)]">
                  <td className="px-3 py-2">
                    <div className="text-[var(--text-primary)]">{a.freshdesk_agent_name || '—'}</div>
                    <div className="text-xs text-[var(--text-tertiary)]">{a.freshdesk_agent_email}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <select value={agentEdits[a.freshdesk_agent_id] ?? ''}
                        onChange={(e) => setAgentEdits((p) => ({ ...p, [a.freshdesk_agent_id]: e.target.value }))}
                        className="px-3 py-1.5 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] bg-white">
                        <option value="">{t('freshdesk.import.unmapped_option')}</option>
                        {users.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
                      </select>
                      {a.mapped_user_id == null && a.suggested_user_id != null &&
                        <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">{t('freshdesk.import.auto_badge')}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => saveAgentMapping(a)} disabled={savingKey === `a${a.freshdesk_agent_id}`}
                      className="px-3 py-1.5 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50">
                      {t('freshdesk.import.save')}
                    </button>
                  </td>
                </tr>
              ))}
              {agents.length === 0 && (
                <tr><td colSpan={3} className="px-3 py-6 text-center text-[var(--text-tertiary)]">{t('freshdesk.import.no_agents_list')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
